import React, { useEffect, useState } from "react";
import { IEvent, IShowCapacityDecision, IWaitlist } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { Modal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./WaitlistTab.module.scss";

interface IProps {
  isShown: boolean;
  anchorWaitlist: IWaitlist;
  event: IEvent;
  onClose(): void;
  onApproved(): void;
}

const BatchApproveModal: React.FC<IProps> = ({
  isShown,
  anchorWaitlist,
  event,
  onClose,
  onApproved,
}) => {
  const [otherWaitlists, setOtherWaitlists] = useState<IWaitlist[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [autoCharge, setAutoCharge] = useState<boolean>(true);
  // Capacity of each show touched by the current selection (from the capacity engine).
  const [capacity, setCapacity] = useState<IShowCapacityDecision[]>([]);
  const [capacityLoading, setCapacityLoading] = useState<boolean>(false);
  // Entries the server refused because the show is full; the club manager may approve anyway.
  const [overCapacity, setOverCapacity] = useState<Record<string, IShowCapacityDecision> | null>(null);
  const [approvedCount, setApprovedCount] = useState<number>(0);

  const selectedIds = [anchorWaitlist._id.toString(), ...Array.from(selected)];
  const selectionKey = selectedIds.join(",");

  useEffect(() => {
    if (!isShown) return;
    setCapacityLoading(true);
    apiService
      .batchApproveWaitlists(anchorWaitlist.event_id, selectionKey.split(","), { dry_run: true })
      .then((result) => setCapacity(result.capacity || []))
      .catch(() => setCapacity([]))
      .finally(() => setCapacityLoading(false));
  }, [isShown, anchorWaitlist.event_id, selectionKey]);

  useEffect(() => {
    if (!isShown) {
      setOverCapacity(null);
      setApprovedCount(0);
    }
  }, [isShown]);

  const selectionExceedsCapacity = capacity.some((c) => !c.fits);

  // Fetch the latest club settings so auto-charge status is accurate
  useEffect(() => {
    if (!isShown) return;
    apiService.getCurrentClub().then((club) => {
      setAutoCharge(club.auto_charge_waitlist !== false);
    }).catch(() => {
      // Fall back to reading from the embedded event.club if available
      setAutoCharge((event.club as any)?.auto_charge_waitlist !== false);
    });
  }, [isShown, event.club]);

  useEffect(() => {
    if (!isShown) return;
    setLoading(true);
    setSelected(new Set());
    apiService
      .getWaitlistsPendingForUser(anchorWaitlist.event_id, (anchorWaitlist as any).user_id)
      .then((results) => {
        const others = results.filter(
          (w) => w._id.toString() !== anchorWaitlist._id.toString()
        );
        setOtherWaitlists(others);
      })
      .catch(() => {
        setOtherWaitlists([]);
      })
      .finally(() => setLoading(false));
  }, [isShown, anchorWaitlist]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ownerName = (anchorWaitlist as any).owner_name || "this user";

  const handleApprove = (ids: string[], allowOverCapacity: boolean) => {
    setSubmitting(true);
    apiService
      .batchApproveWaitlists(anchorWaitlist.event_id, ids, allowOverCapacity ? { allow_over_capacity: true } : undefined)
      .then(({ approved, skipped, over_capacity }) => {
        const totalApproved = approvedCount + approved.length;
        const overIds = Object.keys(over_capacity || {});

        if (!allowOverCapacity && overIds.length > 0) {
          // Entries that fit were approved; hold the rest for an explicit decision.
          if (approved.length > 0) onApproved();
          setApprovedCount(totalApproved);
          setOverCapacity(over_capacity);
          return;
        }

        const otherSkipped = skipped.filter((id) => !overIds.includes(id));
        if (totalApproved === 0) {
          toast.show({
            title: "Waitlist Approval",
            content: `No entries could be approved for ${ownerName} — they may already have been processed.`,
            duration: 10000,
            type: "fail",
          });
          onClose();
          return;
        }
        toast.show({
          title: "Waitlist Approval",
          content: `Successfully approved ${totalApproved} entr${totalApproved === 1 ? "y" : "ies"} for ${ownerName}.${otherSkipped.length ? ` ${otherSkipped.length} entr${otherSkipped.length === 1 ? "y was" : "ies were"} skipped (already processed).` : ""}`,
          duration: 10000,
          type: "success",
        });
        onApproved();
        onClose();
      })
      .catch((e: any) => {
        toast.show({
          title: "Waitlist Approval",
          content:
            e.response?.data?.message ??
            "Something went wrong, please try again later",
          duration: 10000,
          type: "fail",
        });
      })
      .finally(() => setSubmitting(false));
  };

  const formatEntry = (w: IWaitlist) => {
    const parts: string[] = [];
    if ((w as any).call_name) parts.push((w as any).call_name);
    const showType = (w as any).show_type || "";
    const showName = (w as any).show_name || "";
    if (showType || showName) parts.push([showType, showName].filter(Boolean).join(" "));
    const element = (w as any).dog_class?.show_element;
    const level = (w as any).dog_class?.level;
    const sub = [element, level].filter(Boolean).join(", ");
    if (sub) parts.push(sub);
    return parts.join(" — ");
  };

  const allWaitlists = [anchorWaitlist, ...otherWaitlists];
  const labelFor = (id: string) => {
    const w = allWaitlists.find((x) => x._id.toString() === id);
    return w ? formatEntry(w) : "Entry";
  };

  if (overCapacity) {
    const overIds = Object.keys(overCapacity);
    return (
      <Modal
        className="batchApproveModal confirmModal"
        isShown={isShown}
        hide={onClose}
        modalContent={
          <div className={styles.batchApproveWrap}>
            <div className={styles.batchApproveHeader}>
              <h3 className={styles.batchApproveTitle}>Show is at capacity</h3>
              <p className={styles.batchApproveSubtitle}>
                {approvedCount > 0
                  ? `${approvedCount} entr${approvedCount === 1 ? "y was" : "ies were"} approved. `
                  : ""}
                {overIds.length === 1 ? "This entry doesn't" : "These entries don't"} fit within the limit:
              </p>
            </div>

            <div className={styles.batchApproveBody}>
              <ul className={styles.batchApproveOverList}>
                {overIds.map((id) => (
                  <li key={id}>
                    <strong>{labelFor(id)}</strong>
                    <div className={styles.batchApproveCapacityOver}>{overCapacity[id].reason}</div>
                  </li>
                ))}
              </ul>
              <p className={styles.batchApproveHint}>
                Approving anyway will put the show over its limit. Online entries will stay closed.
              </p>
            </div>

            <div className={styles.batchApproveFooter}>
              <Button
                type="button"
                className={styles.batchApproveCancelBtn}
                onClick={onClose}
                disabled={submitting}
              >
                {approvedCount > 0 ? "Done" : "Cancel"}
              </Button>
              <Button
                type="button"
                onClick={() => handleApprove(overIds, true)}
                disabled={submitting}
              >
                {submitting ? "Approving…" : `Approve anyway${overIds.length > 1 ? ` (${overIds.length})` : ""}`}
              </Button>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <Modal
      className="batchApproveModal confirmModal"
      isShown={isShown}
      hide={onClose}
      modalContent={
        <div className={styles.batchApproveWrap}>
          <div className={styles.batchApproveHeader}>
            <h3 className={styles.batchApproveTitle}>Approve Entry</h3>
            <p className={styles.batchApproveSubtitle}>
              {(anchorWaitlist as any).call_name} —{" "}
              {(anchorWaitlist as any).show_type} {(anchorWaitlist as any).show_name}
            </p>
          </div>

          <div className={styles.batchApproveBody}>
            <div className={styles.batchApproveCapacity}>
              {capacityLoading && capacity.length === 0 ? (
                <span className={styles.batchApproveLoading}>Checking capacity…</span>
              ) : (
                capacity.map((c) => (
                  <div
                    key={`${c.show_id}-${c.bucket || ""}`}
                    className={c.fits ? styles.batchApproveCapacityOk : styles.batchApproveCapacityOver}
                  >
                    {c.reason}
                  </div>
                ))
              )}
            </div>

            <div className={styles.batchApproveNotice}>
              {autoCharge ? (
                <>
                  <strong>Auto Charge is enabled.</strong> The card on file for{" "}
                  <strong>{(anchorWaitlist as any).owner_name || "this user"}</strong>{" "}
                  will be charged automatically for all approved entries.
                </>
              ) : (
                <>
                  A payment request will be sent to{" "}
                  <strong>{(anchorWaitlist as any).owner_name || "this user"}</strong>{" "}
                  for all approved entries.
                </>
              )}
            </div>

            {loading ? (
              <p className={styles.batchApproveLoading}>Loading other entries…</p>
            ) : otherWaitlists.length > 0 ? (
              <div className={styles.batchApproveOthers}>
                <p className={styles.batchApproveOthersLabel}>
                  Also approve these waitlisted entries for{" "}
                  <strong>{(anchorWaitlist as any).owner_name || "this user"}</strong>
                  ? They will be included in the same charge.
                </p>
                <ul className={styles.batchApproveList}>
                  {otherWaitlists.map((w) => (
                    <li key={w._id.toString()} className={styles.batchApproveItem}>
                      <label className={styles.batchApproveCheckbox}>
                        <input
                          type="checkbox"
                          checked={selected.has(w._id.toString())}
                          onChange={() => toggleSelect(w._id.toString())}
                        />
                        <span>{formatEntry(w)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <div className={styles.batchApproveFooter}>
            <Button
              type="button"
              className={styles.batchApproveCancelBtn}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => handleApprove(selectedIds, false)}
              disabled={submitting || capacityLoading}
            >
              {submitting
                ? "Approving…"
                : `Approve${selected.size > 0 ? ` (${selected.size + 1})` : ""}${selectionExceedsCapacity ? "…" : ""}`}
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default BatchApproveModal;
